import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  lmsGetStudents, lmsGetTeachers, lmsGetCourses, lmsGetLiveClasses,
  lmsGetExams, lmsGetBatches, lmsGetAuditLog, lmsGetNotices,
  lmsGetClasses, lmsGetSubjects,
} from "../api/lmsApi";


/* ── design tokens (matching HTML reference) ── */
const T = {
  ink:       "#1A1D23",
  inkSoft:   "#6B7280",
  inkMute:   "#9CA3AF",
  pearl:     "#F5F6FA",
  paper:     "#ffffff",
  line:      "#E5E7EB",
  jade:      "#5B4FE9",   /* app primary — indigo */
  jadeSoft:  "#EEF0FF",
  brass:     "#f59e0b",
  brassSoft: "#fef3c7",
  plum:      "#10b981",
  plumSoft:  "#d1fae5",
  amber:     "#f97316",
  amberSoft: "#ffedd5",
  shadow:    "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)",
  red:       "#ef4444",
  redSoft:   "#fee2e2",
};

const SERIF = 'inherit';
const SANS  = 'inherit';

/* ── helpers ── */
const getSchoolId = () => {
  try { const u = JSON.parse(localStorage.getItem("userData") || "{}"); return u.school_id || u.admin_id || null; }
  catch { return null; }
};

const getUserName = () => {
  try { const u = JSON.parse(localStorage.getItem("userData") || "{}"); return u.full_name || u.name || "Admin"; }
  catch { return "Admin"; }
};

function buildMonthly(records, dateKey, months = 6) {
  const now = new Date(); const labels = [], counts = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    labels.push(d.toLocaleString("en-IN", { month: "short" }));
    counts.push(records.filter(r => { const rd = new Date(r[dateKey]); return rd.getFullYear() === d.getFullYear() && rd.getMonth() === d.getMonth(); }).length);
  }
  return { labels, counts };
}

function buildHourly(logs) {
  const now = new Date(); const labels = [], counts = [];
  for (let i = 11; i >= 0; i--) {
    const hr = new Date(now.getTime() - i * 3600000);
    labels.push(`${hr.getHours()}`);
    counts.push(logs.filter(l => { const d = new Date(l.joined_at); return d.getHours() === hr.getHours() && d.toDateString() === hr.toDateString(); }).length);
  }
  return { labels, counts };
}

function buildDailySparkline(records, dateKey, days = 10) {
  const arr = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000);
    arr.push(records.filter(r => new Date(r[dateKey]).toDateString() === d.toDateString()).length);
  }
  return arr;
}

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");
}

/* ── Inline SVG Sparkline ── */
function Sparkline({ data = [], color = T.jade }) {
  if (data.length < 2) return <svg className="dm-spark" viewBox="0 0 200 38" preserveAspectRatio="none" />;
  const max = Math.max(...data, 1);
  const w = 200, h = 38;
  const pts = data.map((v, i) => `${((i / (data.length - 1)) * w).toFixed(1)},${(h - 3 - (v / max) * (h - 6)).toFixed(1)}`).join(" ");
  return (
    <svg className="dm-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ── Inline SVG Trend Chart ── */
function TrendChart({ monthly }) {
  const { labels, counts } = monthly;
  const max = Math.max(...counts, 1);
  const W = 640, H = 230, pl = 36, pr = 10, pt = 20, pb = 30;
  const cW = W - pl - pr;
  const cH = H - pt - pb;
  const gridVals = [max, Math.round(max * 0.75), Math.round(max * 0.5), Math.round(max * 0.25), 0];
  const pts = counts.map((v, i) => {
    const x = pl + (i / (counts.length - 1)) * cW;
    const y = pt + (1 - v / max) * cH;
    return [x, y];
  });
  const linePts = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaD = pts.length ? `M${pts[0][0].toFixed(1)},${(pt + cH).toFixed(1)} ` + pts.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join(" ") + ` L${pts[pts.length - 1][0].toFixed(1)},${(pt + cH).toFixed(1)} Z` : "";

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 400, display: "block" }} role="img" aria-label="Enrollment trend">
      <defs>
        <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={T.jade} stopOpacity=".18" />
          <stop offset="1" stopColor={T.jade} stopOpacity="0" />
        </linearGradient>
      </defs>
      <g style={{ fontFamily: SANS, fontSize: 11, fill: T.inkMute }}>
        {gridVals.map((v, i) => {
          const y = pt + (i / (gridVals.length - 1)) * cH;
          return (
            <g key={i}>
              <line x1={pl} x2={W - pr} y1={y} y2={y} stroke={T.line} strokeDasharray="2 4" />
              <text x={pl - 6} y={y + 4} textAnchor="end">{v}</text>
            </g>
          );
        })}
        <line x1={pl} x2={W - pr} y1={pt + cH} y2={pt + cH} stroke={T.line} />
        {labels.map((l, i) => (
          <text key={i} x={pl + (i / (labels.length - 1)) * cW} y={H - 6} textAnchor="middle">{l}</text>
        ))}
      </g>
      {areaD && <path d={areaD} fill="url(#trendFill)" />}
      <polyline points={linePts} fill="none" stroke={T.jade} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ strokeDasharray: 1200, strokeDashoffset: 1200, animation: "dmDraw 1.4s .3s ease-out forwards" }} />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 6 : 4}
          fill={i === pts.length - 1 ? T.brass : T.paper} stroke={T.jade} strokeWidth="2" />
      ))}
    </svg>
  );
}

/* ── Donut SVG ── */
function Donut({ live, upcoming, ended }) {
  const total = live + upcoming + ended || 1;
  const R = 48, C = 2 * Math.PI * R;
  const segs = [
    { val: live,     color: T.jade,    label: "Live now" },
    { val: upcoming, color: T.brass,   label: "Upcoming" },
    { val: ended,    color: T.inkMute, label: "Ended",   opacity: .55 },
  ];
  let offset = 0;
  const arcs = segs.map(s => {
    const len = (s.val / total) * C;
    const el = { ...s, offset, len };
    offset += len;
    return el;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ position: "relative", width: 190, height: 190, marginBottom: 24 }}>
        <svg viewBox="0 0 120 120" width="190" height="190" role="img" aria-label="Class status">
          <circle cx="60" cy="60" r={R} fill="none" stroke={T.pearl} strokeWidth="12" />
          {arcs.map((a, i) => a.val > 0 && (
            <circle key={i} cx="60" cy="60" r={R} fill="none"
              stroke={a.color} strokeOpacity={a.opacity || 1} strokeWidth="12"
              strokeDasharray={`${a.len.toFixed(2)} ${(C - a.len).toFixed(2)}`}
              strokeDashoffset={(-a.offset + C / 4).toFixed(2)}
              transform="rotate(-90 60 60)" strokeLinecap="round" />
          ))}
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "grid", placeContent: "center", textAlign: "center" }}>
          <span style={{ fontFamily: SERIF, fontSize: 40, lineHeight: 1, color: T.ink }}>{live + upcoming + ended}</span>
          <small style={{ color: T.inkMute, fontSize: 12 }}>total classes</small>
        </div>
      </div>
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10, paddingTop: 16, borderTop: `1px solid ${T.line}` }}>
        {[{ color: T.jade, label: "Live now", val: live }, { color: T.brass, label: "Upcoming", val: upcoming }, { color: T.inkMute, label: "Ended", val: ended, opacity: .55 }].map(s => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: T.inkSoft }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, opacity: s.opacity || 1, flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{s.label}</span>
            <b style={{ color: T.ink, fontWeight: 600, fontFamily: SERIF }}>{s.val}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── card shared styles ── */
const cardStyle = { background: T.paper, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: T.shadow, display: "flex", flexDirection: "column", minWidth: 0 };
const cardHead  = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, minHeight: 64, padding: "16px 24px", borderBottom: `1px solid ${T.line}` };
const cardBody  = { padding: 24, flex: 1 };

const PERIODS = ["All time", "Month", "Week", "Today"];

export default function LmsDashboard() {
  const navigate  = useNavigate();
  const schoolId  = getSchoolId();
  const userName  = getUserName();

  const [allStudents, setAllStudents] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [allExams,    setAllExams]    = useState([]);
  const [allBatches,  setAllBatches]  = useState([]);
  const [allClasses,  setAllClasses]  = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allLogs,     setAllLogs]     = useState([]);
  const [notices,     setNotices]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);

  /* filter bar */
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const [classF,    setClassF]    = useState("");
  const [batchF,    setBatchF]    = useState("");
  const [subjectF,  setSubjectF]  = useState("");
  const [statusF,   setStatusF]   = useState("");
  const [activePeriod, setActivePeriod] = useState("All time");

  /* learning analytics */
  const [aPeriod, setAPeriod] = useState("30 days");
  const [aBatch,  setABatch]  = useState("All batches");

  const load = useCallback(async () => {
    if (!schoolId) return;
    setRefreshing(true);
    try {
      const [sR, lR, eR, bR, cR, sjR, lgR, nR] = await Promise.allSettled([
        lmsGetStudents({ school_id: schoolId }),
        lmsGetLiveClasses({ school_id: schoolId }),
        lmsGetExams({ school_id: schoolId }),
        lmsGetBatches({ school_id: schoolId }),
        lmsGetClasses({ school_id: schoolId }),
        lmsGetSubjects(schoolId),
        lmsGetAuditLog({ school_id: schoolId }),
        lmsGetNotices({ school_id: schoolId }),
      ]);
      const arr = r => { const d = r.status === "fulfilled" ? r.value?.data : null; return d?.data || d || []; };
      setAllStudents(arr(sR));
      setLiveClasses(arr(lR));
      setAllExams(arr(eR));
      setAllBatches(arr(bR));
      setAllClasses(arr(cR));
      setAllSubjects(arr(sjR));
      setAllLogs(arr(lgR));
      setNotices(arr(nR).slice(0, 4));
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  /* period pill → set dates */
  const applyPeriod = (p) => {
    setActivePeriod(p);
    const t = new Date();
    if (p === "Today") { const d = t.toISOString().slice(0, 10); setDateFrom(d); setDateTo(d); }
    else if (p === "Week") { setDateFrom(new Date(t - 6 * 86400000).toISOString().slice(0, 10)); setDateTo(t.toISOString().slice(0, 10)); }
    else if (p === "Month") { setDateFrom(new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10)); setDateTo(t.toISOString().slice(0, 10)); }
    else { setDateFrom(""); setDateTo(""); }
  };

  /* derived */
  const liveNow      = liveClasses.filter(c => c.status === "live");
  const upcoming     = liveClasses.filter(c => c.status === "scheduled");
  const ended        = liveClasses.filter(c => c.status === "ended");
  const studentMonthly = buildMonthly(allStudents, "created_at");
  const hourly         = buildHourly(allLogs);
  const todayJoins     = allLogs.filter(l => new Date(l.joined_at).toDateString() === new Date().toDateString()).length;
  const weekJoins      = allLogs.filter(l => (Date.now() - new Date(l.joined_at)) < 7 * 86400000).length;
  const peakIdx        = hourly.counts.indexOf(Math.max(...hourly.counts));

  const examTypeCount = allExams.reduce((acc, e) => { const k = e.exam_type || "Other"; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const examTotal     = allExams.length || 1;
  const examColors    = [T.plum, T.jade, T.brass, T.amber, T.inkSoft];

  /* learning analytics */
  const aDays    = aPeriod === "7 days" ? 7 : 30;
  const aCutoff  = Date.now() - aDays * 86400000;
  const aBatchId = allBatches.find(b => b.batch_name === aBatch)?.id;
  const aLogs    = allLogs.filter(l => {
    if (new Date(l.joined_at) < aCutoff) return false;
    if (aBatchId) { const lc = liveClasses.find(c => String(c.id) === String(l.live_class_id)); return lc && String(lc.batch_id) === String(aBatchId); }
    return true;
  });
  const aPrevLogs     = allLogs.filter(l => { const d = new Date(l.joined_at); return d >= (aCutoff - aDays * 86400000) && d < aCutoff; });
  const activeLearners = new Set(aLogs.map(l => l.user_id)).size;
  const prevLearners   = new Set(aPrevLogs.map(l => l.user_id)).size;
  const attPct         = aLogs.length ? Math.round((aLogs.filter(l => l.role === "attendee").length / aLogs.length) * 100) : 0;
  const prevAttPct     = aPrevLogs.length ? Math.round((aPrevLogs.filter(l => l.role === "attendee").length / aPrevLogs.length) * 100) : 0;
  const sessionsHeld   = liveClasses.filter(c => new Date(c.scheduled_at) >= aCutoff && (!aBatchId || String(c.batch_id) === String(aBatchId))).length;
  const avgJoins       = sessionsHeld ? Math.round(aLogs.length / sessionsHeld) : 0;

  const pctDiff = (curr, prev) => {
    if (!prev) return curr > 0 ? "+100%" : "0%";
    const d = (((curr - prev) / prev) * 100).toFixed(0);
    return `${Number(d) > 0 ? "+" : ""}${d}%`;
  };

  const laItems = [
    { label: "Active learners",    value: activeLearners,    diff: pctDiff(activeLearners, prevLearners), sub: "unique students",    color: T.plum,  spark: buildDailySparkline(aLogs, "joined_at") },
    { label: "Live attendance",    value: `${attPct}%`,      diff: pctDiff(attPct, prevAttPct),           sub: "of session joins",   color: T.jade,  spark: buildDailySparkline(aLogs.filter(l => l.role === "attendee"), "joined_at") },
    { label: "Total joins",        value: aLogs.length,      diff: pctDiff(aLogs.length, aPrevLogs.length), sub: `in last ${aDays} days`, color: T.jade, spark: buildDailySparkline(aLogs, "joined_at") },
    { label: "Sessions held",      value: sessionsHeld,      diff: "+0%",                                 sub: "live classes",       color: T.brass, spark: buildDailySparkline(liveClasses.filter(c => !aBatchId || String(c.batch_id) === String(aBatchId)), "scheduled_at") },
    { label: "Avg joins / session",value: avgJoins,          diff: "+0%",                                 sub: "per class",          color: T.plum,  spark: buildDailySparkline(aLogs, "joined_at") },
  ];

  const recentLogs  = allLogs.slice(0, 8);
  const thisMonthStudents = allStudents.filter(s => (Date.now() - new Date(s.created_at)) < 30 * 86400000).length;
  const todayStr    = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const batchLabel  = [
    classF   ? allClasses.find(c => String(c.id) === String(classF))?.name   : null,
    batchF   ? allBatches.find(b => String(b.id) === String(batchF))?.batch_name   : null,
    subjectF ? allSubjects.find(s => String(s.id) === String(subjectF))?.subject_name : null,
  ].filter(Boolean).join(" · ") || "all batches";

  /* inject keyframes */
  useEffect(() => {
    if (document.getElementById("dm-kf")) return;
    const s = document.createElement("style");
    s.id = "dm-kf";
    s.textContent = `
      @keyframes dmDraw { to { stroke-dashoffset: 0 } }
      @keyframes dmRise { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:none } }
      .dm-la-num { animation: dmRise .7s cubic-bezier(.2,.7,.2,1) both }
      .dm-spark { margin-top: 20px; width: 100%; height: 40px; overflow: visible; display: block }
    `;
    document.head.appendChild(s);
  }, []);

  if (loading) return (
    <div style={{ background: T.pearl, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: T.inkSoft }}>
      Loading dashboard…
    </div>
  );

  return (
    <div className="lms-content-inner" style={{ background: T.pearl, minHeight: "100vh", color: T.ink }}>

      {/* ── Page Head + period pills + refresh ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 20, lineHeight: 1.2, margin: 0, color: T.ink }}>LMS Dashboard</h2>
          <p style={{ margin: "2px 0 0", color: T.inkSoft, fontSize: 12 }}>{todayStr}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 3, background: T.paper, border: `1px solid ${T.line}`, borderRadius: 8, padding: 3 }}>
            {PERIODS.map(p => (
              <button key={p} onClick={() => applyPeriod(p)} style={{
                height: 30, padding: "0 11px", borderRadius: 6, border: "none",
                background: activePeriod === p ? T.jade : "transparent",
                color: activePeriod === p ? "#fff" : T.inkSoft,
                fontWeight: 600, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
              }}>{p}</button>
            ))}
          </div>
          <button onClick={load} disabled={refreshing} style={{
            display: "flex", alignItems: "center", gap: 5, height: 36, padding: "0 14px", borderRadius: 8,
            border: `1px solid ${T.jade}`, background: T.jade, color: "#fff",
            fontWeight: 600, fontSize: 13, cursor: "pointer", opacity: refreshing ? 0.7 : 1, whiteSpace: "nowrap",
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 12a9 9 0 1 1-2.6-6.4L21 8M21 3v5h-5"/></svg>
            {refreshing ? "…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* ── Filters row — full width ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, width: "100%", background: T.paper, border: `1px solid ${T.line}`, borderRadius: 10, padding: "8px 12px", boxSizing: "border-box" }}>
        {/* Class */}
        <div style={{ flex: 1, minWidth: 0, height: 34, border: `1px solid ${T.line}`, borderRadius: 7, background: T.pearl, overflow: "hidden" }}>
          <select value={classF} onChange={e => { setClassF(e.target.value); setBatchF(""); }}
            style={{ width: "100%", height: "100%", border: 0, outline: "none", background: "none", color: T.ink, fontSize: 13, cursor: "pointer", padding: "0 8px" }}>
            <option value="">All Classes</option>
            {allClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {/* Subject */}
        <div style={{ flex: 1, minWidth: 0, height: 34, border: `1px solid ${T.line}`, borderRadius: 7, background: T.pearl, overflow: "hidden" }}>
          <select value={subjectF} onChange={e => setSubjectF(e.target.value)}
            style={{ width: "100%", height: "100%", border: 0, outline: "none", background: "none", color: T.ink, fontSize: 13, cursor: "pointer", padding: "0 8px" }}>
            <option value="">All Subjects</option>
            {allSubjects.map(s => <option key={s.id} value={s.id}>{s.subject_name}</option>)}
          </select>
        </div>
        {/* Batch */}
        <div style={{ flex: 1, minWidth: 0, height: 34, border: `1px solid ${T.line}`, borderRadius: 7, background: T.pearl, overflow: "hidden" }}>
          <select value={batchF} onChange={e => setBatchF(e.target.value)}
            style={{ width: "100%", height: "100%", border: 0, outline: "none", background: "none", color: T.ink, fontSize: 13, cursor: "pointer", padding: "0 8px" }}>
            <option value="">All Batches</option>
            {allBatches
              .filter(b => !classF || String(b.class_id) === String(classF))
              .map(b => <option key={b.id} value={b.id}>{b.batch_name || b.name}</option>)}
          </select>
        </div>
        {/* Status */}
        <div style={{ flex: 1, minWidth: 0, height: 34, border: `1px solid ${T.line}`, borderRadius: 7, background: T.pearl, overflow: "hidden" }}>
          <select value={statusF} onChange={e => setStatusF(e.target.value)}
            style={{ width: "100%", height: "100%", border: 0, outline: "none", background: "none", color: T.ink, fontSize: 13, cursor: "pointer", padding: "0 8px" }}>
            <option value="">All Status</option>
            <option value="live">Live</option>
            <option value="scheduled">Upcoming</option>
            <option value="ended">Ended</option>
          </select>
        </div>
        {/* Start Date */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 5, border: `1px solid ${T.line}`, borderRadius: 7, padding: "0 8px", height: 34, background: T.pearl }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.inkMute} strokeWidth="1.8" style={{ flexShrink: 0 }}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} aria-label="Start date"
            style={{ border: 0, outline: "none", background: "none", color: T.ink, fontSize: 13, width: "100%", minWidth: 0 }} />
        </div>
        <span style={{ color: T.inkMute, fontSize: 13, flexShrink: 0 }}>–</span>
        {/* End Date */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 5, border: `1px solid ${T.line}`, borderRadius: 7, padding: "0 8px", height: 34, background: T.pearl }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.inkMute} strokeWidth="1.8" style={{ flexShrink: 0 }}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} aria-label="End date"
            style={{ border: 0, outline: "none", background: "none", color: T.ink, fontSize: 13, width: "100%", minWidth: 0 }} />
        </div>
      </div>

      {/* ── Top KPI Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Total Students",   value: allStudents.length,  sub: `+${thisMonthStudents} this month`,  icon: <><circle cx="9" cy="7" r="4"/><path d="M3 21a6 6 0 0 1 12 0"/><path d="M19 8v6M16 11h6"/></>,  bg: T.jadeSoft,  color: T.jade,  accent: T.jade },
          { label: "Live Classes Now", value: liveNow.length,      sub: `${upcoming.length} upcoming`,       icon: <><rect x="2" y="6" width="14" height="12" rx="2"/><path d="m16 10 6-3v10l-6-3"/></>,            bg: T.plumSoft,  color: T.plum,  accent: T.plum },
          { label: "Total Exams",      value: allExams.length,     sub: "All time",                          icon: <><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 11h6M9 15h4"/></>,              bg: T.brassSoft, color: T.brass, accent: T.brass },
          { label: "Joins Today",      value: todayJoins,          sub: `${weekJoins} this week`,            icon: <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>,                                                           bg: T.amberSoft, color: T.amber, accent: T.amber },
        ].map(({ label, value, sub, icon, bg, color, accent }) => (
          <div key={label} style={{ background: T.paper, border: `1px solid ${T.line}`, borderLeft: `4px solid ${accent}`, borderRadius: 12, boxShadow: T.shadow, padding: "18px 20px", display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: bg, color, display: "grid", placeItems: "center", flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{icon}</svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: T.inkSoft, marginBottom: 2, textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600 }}>{label}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: T.ink, lineHeight: 1.1 }}>{value}</div>
              <div style={{ fontSize: 12, color: T.inkMute, marginTop: 2 }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Learning Analytics ── */}
      <article style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={{ ...cardHead, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: "-.01em", color: T.ink }}>Learning analytics</h3>
            <small style={{ color: T.inkMute, fontSize: 12 }}>{aBatch}, last {aPeriod}</small>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", border: `1px solid ${T.line}`, borderRadius: 10, padding: "0 12px", height: 40, background: T.paper }}>
              <select value={aBatch} onChange={e => setABatch(e.target.value)}
                style={{ border: 0, background: "none", outline: "none", color: T.ink, fontFamily: SANS, fontSize: 13, cursor: "pointer" }}>
                <option>All batches</option>
                {allBatches.map(b => <option key={b.id}>{b.batch_name}</option>)}
              </select>
            </label>
            <div style={{ display: "flex", height: 40, background: T.pearl, borderRadius: 10, padding: 4, gap: 2 }} role="group">
              {["7 days", "30 days"].map(p => (
                <button key={p} onClick={() => setAPeriod(p)} style={{
                  border: 0, borderRadius: 8, padding: "0 16px", fontFamily: SANS, fontWeight: 600, fontSize: 13, cursor: "pointer",
                  background: aPeriod === p ? T.paper : "none",
                  color: aPeriod === p ? T.ink : T.inkSoft,
                  boxShadow: aPeriod === p ? "0 1px 3px rgba(0,0,0,.08)" : "none",
                }}>{p}</button>
              ))}
            </div>
          </div>
        </div>
        {/* 5 KPI columns */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)" }}>
          {laItems.map((item, i) => (
            <div key={item.label} style={{ padding: "24px 24px 28px", borderLeft: i === 0 ? "none" : `1px solid ${T.line}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, fontSize: 13, color: T.inkSoft, minHeight: 22 }}>
                <span>{item.label}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: T.jade, background: T.jadeSoft, padding: "2px 8px", borderRadius: 99, whiteSpace: "nowrap" }}>{item.diff}</span>
              </div>
              <div className="dm-la-num" style={{ fontFamily: SERIF, fontSize: 48, lineHeight: 1.05, margin: "16px 0 4px", color: T.ink, animationDelay: `${i * 0.06}s` }}>{item.value}</div>
              <small style={{ color: T.inkMute, fontSize: 12 }}>{item.sub}</small>
              <Sparkline data={item.spark} color={item.color} />
            </div>
          ))}
        </div>
      </article>

      {/* ── Row 1: Enrollment Trend + Class Status ── */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24, marginBottom: 24 }}>
        <article style={cardStyle}>
          <div style={cardHead}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Student enrollment</h3>
              <small style={{ color: T.inkMute, fontSize: 12 }}>Last 6 months</small>
            </div>
            <button onClick={() => navigate("/lms/students")} style={{ fontWeight: 600, fontSize: 13, color: T.jade, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
              View all <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
            </button>
          </div>
          <div style={cardBody}>
            <div style={{ display: "flex", gap: 40, marginBottom: 20 }}>
              <div><span style={{ fontFamily: SERIF, fontSize: 32, color: T.ink }}>{thisMonthStudents}</span><small style={{ display: "block", color: T.inkMute, fontSize: 12 }}>New this month</small></div>
              <div><span style={{ fontFamily: SERIF, fontSize: 32, color: T.ink }}>{allStudents.length}</span><small style={{ display: "block", color: T.inkMute, fontSize: 12 }}>Total students</small></div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <TrendChart monthly={studentMonthly} />
            </div>
          </div>
        </article>

        <article style={cardStyle}>
          <div style={cardHead}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Class status</h3>
              <small style={{ color: T.inkMute, fontSize: 12 }}>All time</small>
            </div>
          </div>
          <div style={cardBody}>
            <Donut live={liveNow.length} upcoming={upcoming.length} ended={ended.length} />
          </div>
        </article>
      </div>

      {/* ── Row 2: Activity + Exam Distribution + Session Pulse ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginBottom: 24 }}>

        {/* Today's Activity — CSS bars */}
        <article style={cardStyle}>
          <div style={cardHead}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Today's activity</h3>
              <small style={{ color: T.inkMute, fontSize: 12 }}>Joins per hour</small>
            </div>
          </div>
          <div style={cardBody}>
            {(() => {
              const max = Math.max(...hourly.counts, 1);
              return (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(12,1fr)", alignItems: "end", gap: 6, height: 170, borderBottom: `1px solid ${T.line}`, position: "relative" }}>
                    {hourly.counts.map((v, i) => (
                      <div key={i} title={`${hourly.labels[i]}:00 — ${v} joins`} style={{
                        background: i === peakIdx ? T.jade : T.jadeSoft,
                        borderRadius: "6px 6px 2px 2px",
                        height: `${Math.max((v / max) * 100, v > 0 ? 3 : 0)}%`,
                        minHeight: v > 0 ? 3 : 0,
                      }} />
                    ))}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(12,1fr)", gap: 6, marginTop: 8 }}>
                    {hourly.labels.map(l => <span key={l} style={{ fontSize: 10.5, color: T.inkMute, textAlign: "center" }}>{l}</span>)}
                  </div>
                  <p style={{ margin: "20px 0 0", paddingTop: 16, borderTop: `1px solid ${T.line}`, fontSize: 13, color: T.inkSoft }}>
                    Busiest hour was <b style={{ color: T.ink }}>{hourly.labels[peakIdx]}:00–{Number(hourly.labels[peakIdx]) + 1}:00</b> with {hourly.counts[peakIdx]} joins.
                  </p>
                </>
              );
            })()}
          </div>
        </article>

        {/* Exam Distribution — CSS bars */}
        <article style={cardStyle}>
          <div style={cardHead}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Exam distribution</h3>
              <small style={{ color: T.inkMute, fontSize: 12 }}>By type</small>
            </div>
            <button onClick={() => navigate("/lms/exams")} style={{ fontWeight: 600, fontSize: 13, color: T.jade, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
              View all <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
            </button>
          </div>
          <div style={cardBody}>
            {Object.keys(examTypeCount).length === 0
              ? <p style={{ color: T.inkMute, fontSize: 13 }}>No exams created yet.</p>
              : Object.entries(examTypeCount).map(([type, cnt], i) => {
                  const pct = Math.round((cnt / examTotal) * 100);
                  return (
                    <div key={type} style={{ marginBottom: 24 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8 }}>
                        <b style={{ color: T.ink }}>{type}</b>
                        <span style={{ color: T.inkSoft }}>{cnt} exam · {pct}%</span>
                      </div>
                      <div style={{ height: 10, background: T.pearl, borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 99, background: examColors[i % examColors.length] }} />
                      </div>
                    </div>
                  );
                })
            }
            <p style={{ margin: "20px 0 0", paddingTop: 16, borderTop: `1px solid ${T.line}`, fontSize: 13, color: T.inkSoft }}>
              {allExams.length} exam{allExams.length !== 1 ? "s" : ""} created total.
            </p>
          </div>
        </article>

        {/* Session Pulse */}
        <article style={cardStyle}>
          <div style={cardHead}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Session pulse</h3>
          </div>
          <div style={{ ...cardBody, display: "flex", flexDirection: "column" }}>
            {[
              { label: "Joins today",        sub: "Students and teachers", value: todayJoins, iconBg: T.jadeSoft,  iconColor: T.jade,
                icon: <path d="M22 12h-4l-3 9L9 3l-3 9H2" /> },
              { label: "Joins this week",    sub: "Since Monday",          value: weekJoins,  iconBg: T.plumSoft,  iconColor: T.plum,
                icon: <><path d="m22 7-8.5 8.5-5-5L2 17M16 7h6v6"/></> },
              { label: "Classes live now",   sub: "In progress",           value: liveNow.length, iconBg: T.brassSoft, iconColor: T.brass,
                icon: <><rect x="2" y="6" width="14" height="12" rx="2"/><path d="m16 10 6-3v10l-6-3"/></> },
              { label: "Upcoming sessions",  sub: "Scheduled",             value: upcoming.length, iconBg: T.amberSoft, iconColor: T.amber,
                icon: <path d="M13 2 3 14h9l-1 8 10-12h-9z" /> },
            ].map(({ label, sub, value, iconBg, iconColor, icon }, i) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 0", borderTop: i === 0 ? "none" : `1px solid ${T.line}` }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: iconBg, color: iconColor, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{icon}</svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: T.ink }}>{label}</p>
                  <small style={{ color: T.inkMute, fontSize: 12 }}>{sub}</small>
                </div>
                <span style={{ fontFamily: SERIF, fontSize: 30, color: T.ink, marginLeft: "auto" }}>{value}</span>
              </div>
            ))}
          </div>
        </article>
      </div>

      {/* ── Row 3: Attendance + Live Sessions + Quick Actions ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr 1fr", gap: 24, marginBottom: 24 }}>

        {/* Recent Attendance */}
        <article style={cardStyle}>
          <div style={cardHead}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Recent attendance</h3>
            <button onClick={() => navigate("/lms/audit-log")} style={{ fontWeight: 600, fontSize: 13, color: T.jade, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
              View all <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
            </button>
          </div>
          <div style={{ padding: 0, flex: 1, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: SANS }}>
              <thead>
                <tr style={{ background: `color-mix(in srgb,${T.pearl} 55%,transparent)` }}>
                  {["Name", "Session", "Role", "Joined"].map(h => (
                    <th key={h} style={{ textAlign: "left", fontWeight: 600, color: T.inkMute, fontSize: 12, height: 44, padding: "0 16px", background: "none", borderBottom: `1px solid ${T.line}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentLogs.length === 0
                  ? <tr><td colSpan={4} style={{ padding: 24, textAlign: "center", color: T.inkMute }}>No records yet</td></tr>
                  : recentLogs.map((l, i) => {
                      const isStudent = l.role === "attendee";
                      const bg  = isStudent ? T.jadeSoft : T.plumSoft;
                      const col = isStudent ? T.jade : T.plum;
                      return (
                        <tr key={l.id || i} style={{ borderBottom: `1px solid ${T.line}` }}>
                          <td style={{ height: 56, padding: "0 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, color: T.ink }}>
                              <span style={{ width: 30, height: 30, borderRadius: "50%", background: bg, color: col, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, fontFamily: SANS }}>
                                {initials(l.user_name)}
                              </span>
                              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>{l.user_name || "Unknown"}</span>
                            </div>
                          </td>
                          <td style={{ height: 56, padding: "0 16px", color: T.inkSoft, whiteSpace: "nowrap" }}>
                            {l.live_class_title || `Class #${l.live_class_id}`}
                          </td>
                          <td style={{ height: 56, padding: "0 16px" }}>
                            <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 99, background: bg, color: col }}>
                              {isStudent ? "Student" : "Teacher"}
                            </span>
                          </td>
                          <td style={{ height: 56, padding: "0 16px", color: T.inkSoft, whiteSpace: "nowrap" }}>
                            {new Date(l.joined_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}
                          </td>
                        </tr>
                      );
                    })
                }
              </tbody>
            </table>
          </div>
        </article>

        {/* Live Sessions */}
        <article style={cardStyle}>
          <div style={cardHead}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Live sessions</h3>
            <button onClick={() => navigate("/lms/live-classes")} style={{ fontWeight: 600, fontSize: 13, color: T.jade, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
              View all <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
            </button>
          </div>
          {[...liveNow, ...upcoming].length === 0
            ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "32px 24px", gap: 8 }}>
                <div style={{ width: 84, height: 84, borderRadius: "50%", border: `1px dashed ${T.brass}`, display: "grid", placeItems: "center", color: T.brass, marginBottom: 12 }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8a6 6 0 0 1 0 8.4M7.8 16.2a6 6 0 0 1 0-8.4M19.1 4.9a10 10 0 0 1 0 14.2M4.9 19.1a10 10 0 0 1 0-14.2"/></svg>
                </div>
                <h4 style={{ margin: 0, fontFamily: SERIF, fontWeight: 400, fontSize: 20, color: T.ink }}>No classes are live</h4>
                <p style={{ margin: "0 0 16px", color: T.inkSoft, maxWidth: "28ch", fontSize: 13 }}>Sessions appear here the moment a teacher starts one.</p>
                <button onClick={() => navigate("/lms/live-classes")} style={{ height: 40, padding: "0 16px", borderRadius: 10, background: T.jade, border: `1px solid ${T.jade}`, color: "#fff", fontFamily: SANS, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                  Schedule a live class
                </button>
              </div>
            )
            : [...liveNow, ...upcoming].slice(0, 6).map((c, i) => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 24px", borderTop: i === 0 ? "none" : `1px solid ${T.line}` }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.status === "live" ? T.jade : T.brass, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</div>
                    <div style={{ fontSize: 11, color: T.inkMute, marginTop: 1 }}>{[c.class_name, c.batch_name].filter(Boolean).join(" · ")}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 99, background: c.status === "live" ? T.jadeSoft : T.brassSoft, color: c.status === "live" ? T.jade : T.brass, flexShrink: 0 }}>
                    {c.status === "live" ? "● LIVE" : "Soon"}
                  </span>
                </div>
              ))
          }
        </article>

        {/* Quick Actions */}
        <article style={cardStyle}>
          <div style={cardHead}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Quick actions</h3>
          </div>
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignContent: "start" }}>
            {[
              { label: "Schedule class", bg: T.jadeSoft,  color: T.jade,  path: "/lms/live-classes", icon: <><rect x="2" y="6" width="14" height="12" rx="2"/><path d="m16 10 6-3v10l-6-3"/></> },
              { label: "Create exam",    bg: T.brassSoft, color: T.brass, path: "/lms/exams",        icon: <><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 11h6M9 15h4"/></> },
              { label: "Add student",    bg: T.plumSoft,  color: T.plum,  path: "/lms/students",     icon: <><circle cx="9" cy="8" r="4"/><path d="M2 21a7 7 0 0 1 14 0M19 8v6M16 11h6"/></> },
              { label: "Add teacher",    bg: T.amberSoft, color: T.amber, path: "/lms/teachers",     icon: <><path d="M22 10 12 5 2 10l10 5 10-5z"/><path d="M6 12v5c3 2 9 2 12 0v-5"/></> },
              { label: "Manage courses", bg: T.jadeSoft,  color: T.jade,  path: "/lms/courses",      icon: <path d="M2 4h7a3 3 0 0 1 3 3v14a2 2 0 0 0-2-2H2zM22 4h-7a3 3 0 0 0-3 3v14a2 2 0 0 1 2-2h8z"/> },
              { label: "Post notice",    bg: T.brassSoft, color: T.brass, path: "/lms/notice-board", icon: <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/> },
              { label: "Attendance log", bg: T.plumSoft,  color: T.plum,  path: "/lms/audit-log",    icon: <path d="M22 12h-4l-3 9L9 3l-3 9H2"/> },
              { label: "Staff chat",     bg: T.amberSoft, color: T.amber, path: "/lms/staff-chat",   icon: <path d="M21 12a8 8 0 0 1-11.6 7.1L3 21l1.9-6.4A8 8 0 1 1 21 12z"/> },
            ].map(({ label, bg, color, path, icon }) => (
              <button key={label} onClick={() => navigate(path)} style={{
                display: "flex", flexDirection: "column", gap: 10, padding: 16, border: `1px solid ${T.line}`,
                borderRadius: 12, fontWeight: 600, fontSize: 13, textAlign: "left", cursor: "pointer",
                background: T.paper, color: T.ink, fontFamily: SANS,
                transition: "border-color .15s, background .15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.brass; e.currentTarget.style.background = `${T.brassSoft}55`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.background = T.paper; }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, background: bg, color, display: "grid", placeItems: "center" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{icon}</svg>
                </span>
                {label}
              </button>
            ))}
          </div>
        </article>
      </div>

      {/* ── Notices ── */}
      {notices.length > 0 && (
        <article style={cardStyle}>
          <div style={cardHead}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Recent notices</h3>
            <button onClick={() => navigate("/lms/notice-board")} style={{ fontWeight: 600, fontSize: 13, color: T.jade, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
              View all <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(notices.length, 4)},1fr)` }}>
            {notices.map((n, i) => (
              <div key={n.id} style={{ padding: "20px 24px", borderLeft: i === 0 ? "none" : `1px solid ${T.line}` }}>
                <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: 14, color: T.ink, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.brass, flexShrink: 0, display: "inline-block" }} />
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.title}</span>
                </p>
                <small style={{ color: T.inkMute, paddingLeft: 14, fontSize: 12 }}>
                  {new Date(n.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </small>
              </div>
            ))}
          </div>
        </article>
      )}

    </div>
  );
}
